import React, { memo } from "react";
import { makeStyles } from "@mui/styles";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import clsx from "clsx";
import { isOnlyLink, StorageUtil, convertString2JSON } from "utils";
import LinkPreview from "./LinkPreview";
import { useConversationContext } from "../ConversationContext";
import ChatTypography from "./ChatTypography";
import { getCreatedTimeText, useMessageStyle } from "./ChatItem.helper";
import { AppAvatar } from "components";
import { SCROLL_TRIGGER_CLASS } from "../useQueryMessage";
import { AppConstant, SystemConstant, LangConstant } from "const";
import { PinFilledIcon, TimerIcon } from "components/icons";
import EditMessage from "./EditMessage";
import ThreadParentLine from "./ThreadParentLine";
import DecryptMessageView from "./DecryptMessageView";
import { useTranslation } from "react-i18next";
import { getInteractor } from "services/local.service";
import CipherMessageManager from "pubsub/services/cipher/CipherMessageManager";

const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;
const cipherMessageManager = new CipherMessageManager();

const MessengerPeople = ({ message, isMine, isEditing, onCloseEdit, isDecryptFailed, setIsDecryptFailed }) => {
  const hasExternalLink = isOnlyLink(message.content);
  const isAvatar = Boolean(message.isAvatar);
  const prefixKey = StorageUtil.getCurrentPrefixKey();

  const retry = async () => {
    const messageOptions = convertString2JSON(message.options, {});
    const encryptVersion = messageOptions.encrypt_version;
    const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
    const indexSendNumber = messageOptions.index_send_number;
    if (indexSendNumber === null || indexSendNumber === undefined) return;
    const groupDetail = await getInteractor(prefixKey).LocalGroupService.get(message.groupId);
    cipherMessageManager.decryptNormalMessage.decryptRecoverOneMessage(
      message,
      groupDetail, 
      encryptTypeProtocol, 
      prefixKey);
  };

  return (
    <>
      {isMine ? (
        <MyMessage
          message={message}
          hasExternalLink={hasExternalLink}
          messageMode={isEditing ? AppConstant.MESSAGE_MODE.editing : AppConstant.MESSAGE_MODE.normal}
          onCloseEdit={onCloseEdit}
          isDecryptFailed={isDecryptFailed}
          setIsDecryptFailed={setIsDecryptFailed}
          retry={retry}
        />
      ) : (
        <OtherMessage
          message={message}
          hasExternalLink={hasExternalLink}
          isAvatar={isAvatar}
          isDecryptFailed={isDecryptFailed}
          setIsDecryptFailed={setIsDecryptFailed}
          retry={retry}
        />
      )}
    </>
  );
};

export default memo(MessengerPeople);

export const MyMessage = ({
  message,
  hasExternalLink,
  customStyles,
  messageMode = AppConstant.MESSAGE_MODE.normal,
  onCloseEdit,
  isDecryptFailed,
  setIsDecryptFailed,
  retry = () => {},
}) => {
  const classes = useStyles();
  const messengerClasses = useMessageStyle();

  const isPreview = messageMode === AppConstant.MESSAGE_MODE.preview;
  const isEditing = messageMode === AppConstant.MESSAGE_MODE.editing;
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);

  return (
    <Stack direction="column">
      {!isPreview && Boolean(message.sendAsDirectMessage) && <ThreadParentLine message={message} />}

      <Stack direction="row" justifyContent="end">
        {!Boolean(isPreview) && (message.isPin || message.isDisappear) && (
          <Stack justifyContent="space-between" mr={0.5}>
            <Box>{message.isPin && <PinFilledIcon />}</Box>
            {message.isDisappear && <TimerIcon />}
          </Stack>
        )}
        {isDecryptFailed && (
          <Stack direction="row" alignItems="center">
            <Box className={classes.chatReSend} onClick={() => retry()}>
              <IconButton color="primary" className={classes.chatReSendButton}>
                <Refresh style={{ fontSize: 12 }} />
              </IconButton>
              <Typography color="primary" className={classes.chatReSendText}>
                {getLabel(LangConstant.TXT_RETRY)}
              </Typography>
            </Box>
          </Stack>
        )}

        <Stack
          direction="column"
          className={clsx(
            messengerClasses.messengerRoot,
            messengerClasses.myMessage,
            {
              [classes.previewLink]: hasExternalLink,
            },
            customStyles,
            SCROLL_TRIGGER_CLASS,
          )}
        >
          {isEditing ? (
            <EditMessage message={message} onCloseEdit={onCloseEdit} />
          ) : (
            <>
              {message?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover ||
              message?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed ? (
                <DecryptMessageView
                  created={message.created}
                  isMine={true}
                  isDecryptFailed={isDecryptFailed}
                  setIsDecryptFailed={setIsDecryptFailed}
                />
              ) : (
                <ChatTypography
                  messageContent={message.content}
                  className={clsx(messengerClasses.spacing, "preview-chat")}
                  messageId={message.id}
                  messageSourceId={message.sourceId}
                />
              )}
              {hasExternalLink && (
                <LinkPreview
                  message={{
                    ...message,
                    content: message.content,
                  }}
                />
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const OtherMessage = ({ message, hasExternalLink, isAvatar, isDecryptFailed, setIsDecryptFailed, retry }) => {
  const classes = useStyles();
  const messengerClasses = useMessageStyle();
  const msgCreatedTime = getCreatedTimeText(message);
  const { groupDetail } = useConversationContext();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);
  return (
    <Stack direction="row" spacing={2}>
      <AppAvatar
        avatarId={message.avatarId}
        size={40}
        className={clsx(!isAvatar && messengerClasses.hiddenAvatar)}
        isServerLogo={groupDetail.isBot}
      />

      <Box>
        {msgCreatedTime && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {message.isDisappear && <TimerIcon />}
            <Typography
              className={clsx("ellipsis", messengerClasses.createdTimeText, "preview-time-text", "msg-sender-time")}
            >
              {msgCreatedTime}
            </Typography>
          </Stack>
        )}

        {Boolean(message.sendAsDirectMessage) && <ThreadParentLine message={message} />}

        <Stack direction="row">
          <Stack
            direction="column"
            className={clsx(messengerClasses.messengerRoot, SCROLL_TRIGGER_CLASS, {
              [classes.previewLink]: hasExternalLink,
            })}
          >
            {message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover ||
            message?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed ? (
              <DecryptMessageView
                created={message.created}
                isDecryptFailed={isDecryptFailed}
                setIsDecryptFailed={setIsDecryptFailed}
              />
            ) : (
              <ChatTypography
                messageContent={message.content}
                mentions={message.mentions}
                className={clsx(messengerClasses.spacing, "preview-chat")}
                messageId={message.id}
                messageSourceId={message.sourceId}
              />
            )}
            {hasExternalLink && <LinkPreview message={message} />}
          </Stack>
          {message.isPin && <PinFilledIcon />}
          {isDecryptFailed && (
            <Stack direction="row" alignItems="center">
              <Box className={classes.chatReSend} onClick={() => retry()}>
                <IconButton color="primary" className={classes.chatReSendButton}>
                  <Refresh style={{ fontSize: 12 }} />
                </IconButton>
                <Typography color="primary" className={classes.chatReSendText}>
                  {getLabel(LangConstant.TXT_RETRY)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles({
  previewLink: {
    width: 300,
    maxWidth: "100%",
  },
  chatReSendButton: {
    padding: 0,
    lineHeight: "20px",
    marginRight: 2,
  },
  chatReSendText: {
    fontSize: 12,
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
  chatReSend: {
    height: "20px",
    display: "flex",
    lineHeight: "20px",
    cursor: "pointer",
    margin: 8,
  },
});
